import React from 'react';
import {loadable} from 'lib/loadable/route';
import {Route, IndexRedirect, IndexRoute} from 'react-router';
import sessionStore from 'client/Session/SessionStore';
import {resource} from '@albert-io/json-api-framework/request/builder';
import {getFeatureFlagStatus} from 'client/components/FeatureFlag/FeatureFlag.react';

import Report from './Report/Report.react';
import ReportSelector from './ReportSelector/ReportSelector.react';
import {TOP_LEVEL_FILTERS} from './reports.types';

const Reports = loadable(() => import(/* webpackChunkName: "Reports" */ './Reports.react'));

/*
  If a user is navigating to /reports/:classroom_id/standards and the URL does NOT already have
  standard sets as a query param, auto populate standard sets with the user's
   personalized preferences (if they exist). The "personalization" queryParam is used
   to display a message in the standard set filter that the preferences were auto applied.
   Once a user makes any manual changes to the filter, this queryParam goes away.
*/
const redirectToUserPreferences = async (nextState, replace, callback) => {
  try {
    if (nextState.location.query.standardSets) {
      callback();
    } else {
      /* TODO: remove block of code upon QA approval of content personalization feature flag */
      const isPersonalizationEnabled = await getFeatureFlagStatus('content_personalization');
      if (!isPersonalizationEnabled) {
        callback();
        /* end TODO */
      } else {
        const standardSets = await fetchStandardSets();
        if (standardSets.size) {
          const queryParams = new URLSearchParams({
            ...nextState.location.query,
            standardSets: standardSets.map((standardSet) => standardSet.getId()).join(','),
            personalization: true
          });
          replace(`${nextState.location.pathname}?${queryParams.toString()}`);
        }
        callback();
      }
    }
  } catch (e) {
    // no-op if there's an error loading standard sets
    callback();
  }
};

function fetchStandardSets() {
  return resource('standard_sets_v1')
    .mandarkEndpoint(['standard_sets_v1'])
    .pageSize(100)
    .withMeta('standard_set_v1')
    .customQuery({
      meta: {
        context: {
          user: {
            id: sessionStore.getUserId(),
            personalization_settings: true
          }
        }
      }
    })
    .filter({
      'meta.included_in_personalization_settings': true
    })
    .getResourcePromise();
}

const maybeRedirectFromLegacyUrl = ({location, params}, replace) => {
  const {search, pathname} = location;
  const urlParts = pathname.split('/').slice(2);

  if (urlParts.length >= 1) {
    const maybeId = params.topLevelFilterType;
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    if (uuidRegex.test(maybeId)) {
      replace(`/reports/classrooms/${urlParts.join('/')}${search}`);
    } else if (maybeId.startsWith('teacher-')) {
      replace(`/reports/teachers/${maybeId.slice(8)}/${urlParts.slice(1).join('/')}${search}`);
    } else if (!TOP_LEVEL_FILTERS.includes(maybeId) || urlParts.length === 1) {
      replace('/reports');
    }
  }
};

const buildRoutes = (needsReportSelector) => {
  return (
    <>
      {/* Students dimension */}
      <Route component={needsReportSelector ? ReportSelector : null} path='students'>
        <IndexRoute component={Report} />
        <Route component={Report} path=':studentId'>
          <IndexRedirect to='assignments' />
          <Route component={Report} path='questions' />
          <Route component={Report} path='questions/:questionId'>
            <IndexRedirect to='assignments' />
            <Route component={Report} path='assignments' />
            <Route component={Report} path='assignments/:assignmentId'>
              <IndexRedirect to='standards' />
              <Route component={Report} path='standards' onEnter={redirectToUserPreferences} />
            </Route>
            <Route component={Report} path='standards' onEnter={redirectToUserPreferences} />
            <Route component={Report} path='standards/:standardId'>
              <IndexRedirect to='assignments' />
              <Route component={Report} path='assignments' />
            </Route>
          </Route>
          <Route component={Report} path='assignments' />
          <Route component={Report} path='assignments/:assignmentId'>
            <IndexRedirect to='questions' />
            <Route component={Report} path='questions' />
            <Route component={Report} path='questions/:questionId'>
              <IndexRedirect to='standards' />
              <Route component={Report} path='standards' onEnter={redirectToUserPreferences} />
            </Route>
            <Route component={Report} path='standards' onEnter={redirectToUserPreferences} />
            <Route component={Report} path='standards/:standardId'>
              <IndexRedirect to='questions' />
              <Route component={Report} path='questions' />
            </Route>
          </Route>
          <Route component={Report} path='standards' onEnter={redirectToUserPreferences} />
          <Route component={Report} path='standards/:standardId'>
            <IndexRedirect to='questions' />
            <Route component={Report} path='questions' />
            <Route component={Report} path='questions/:questionId'>
              <IndexRedirect to='assignments' />
              <Route component={Report} path='assignments' />
            </Route>
            <Route component={Report} path='assignments' />
            <Route component={Report} path='assignments/:assignmentId'>
              <IndexRedirect to='questions' />
              <Route component={Report} path='questions' />
            </Route>
          </Route>
        </Route>
      </Route>

      {/* Assignments dimension */}
      <Route component={needsReportSelector ? ReportSelector : null} path='assignments'>
        <IndexRoute component={Report} />
        <Route component={Report} path=':assignmentId'>
          <IndexRedirect to='students' />
          <Route component={Report} path='questions' />
          <Route component={Report} path='questions/:questionId'>
            <IndexRedirect to='standards' />
            <Route component={Report} path='standards' onEnter={redirectToUserPreferences} />
            <Route component={Report} path='standards/:standardId'>
              <IndexRedirect to='students' />
              <Route component={Report} path='students' />
            </Route>
            <Route component={Report} path='students' />
            <Route component={Report} path='students/:studentId'>
              <IndexRedirect to='standards' />
              <Route component={Report} path='standards' onEnter={redirectToUserPreferences} />
            </Route>
          </Route>
          <Route component={Report} path='students' />
          <Route component={Report} path='students/:studentId'>
            <IndexRedirect to='questions' />
            <Route component={Report} path='questions' />
            <Route component={Report} path='questions/:questionId'>
              <IndexRedirect to='standards' />
              <Route component={Report} path='standards' onEnter={redirectToUserPreferences} />
            </Route>
            <Route component={Report} path='standards' onEnter={redirectToUserPreferences} />
            <Route component={Report} path='standards/:standardId'>
              <IndexRedirect to='questions' />
              <Route component={Report} path='questions' />
            </Route>
          </Route>
          <Route component={Report} path='standards' onEnter={redirectToUserPreferences} />
          <Route component={Report} path='standards/:standardId'>
            <IndexRedirect to='questions' />
            <Route component={Report} path='questions' />
            <Route component={Report} path='questions/:questionId'>
              <IndexRedirect to='students' />
              <Route component={Report} path='students' />
            </Route>
            <Route component={Report} path='students' />
            <Route component={Report} path='students/:studentId'>
              <IndexRedirect to='questions' />
              <Route component={Report} path='questions' />
            </Route>
          </Route>
        </Route>
      </Route>

      {/* Questions dimension */}
      <Route component={needsReportSelector ? ReportSelector : null} path='questions'>
        <IndexRoute component={Report} />
        <Route component={Report} path=':questionId'>
          <IndexRedirect to='students' />
          <Route component={Report} path='assignments' />
          <Route component={Report} path='assignments/:assignmentId'>
            <IndexRedirect to='standards' />
            <Route component={Report} path='standards' onEnter={redirectToUserPreferences} />
            <Route component={Report} path='standards/:standardId'>
              <IndexRedirect to='students' />
              <Route component={Report} path='students' />
            </Route>
            <Route component={Report} path='students' />
            <Route component={Report} path='students/:studentId'>
              <IndexRedirect to='standards' />
              <Route component={Report} path='standards' onEnter={redirectToUserPreferences} />
            </Route>
          </Route>
          <Route component={Report} path='standards' onEnter={redirectToUserPreferences} />
          <Route component={Report} path='standards/:standardId'>
            <IndexRedirect to='assignments' />
            <Route component={Report} path='assignments' />
            <Route component={Report} path='assignments/:assignmentId'>
              <IndexRedirect to='students' />
              <Route component={Report} path='students' />
            </Route>
            <Route component={Report} path='students' />
            <Route component={Report} path='students/:studentId'>
              <IndexRedirect to='assignments' />
              <Route component={Report} path='assignments' />
            </Route>
          </Route>
          <Route component={Report} path='students' />
          <Route component={Report} path='students/:studentId'>
            <IndexRedirect to='assignments' />
            <Route component={Report} path='assignments' />
            <Route component={Report} path='assignments/:assignmentId'>
              <IndexRedirect to='standards' />
              <Route component={Report} path='standards' onEnter={redirectToUserPreferences} />
            </Route>
            <Route component={Report} path='standards' onEnter={redirectToUserPreferences} />
            <Route component={Report} path='standards/:standardId'>
              <IndexRedirect to='assignments' />
              <Route component={Report} path='assignments' />
            </Route>
          </Route>
        </Route>
      </Route>

      {/* Standards dimension */}
      <Route
        component={needsReportSelector ? ReportSelector : null}
        path='standards'
        onEnter={redirectToUserPreferences}
      >
        <IndexRoute component={Report} />
        <Route component={Report} path=':standardId'>
          <IndexRedirect to='students' />
          <Route component={Report} path='assignments' />
          <Route component={Report} path='assignments/:assignmentId'>
            <IndexRedirect to='questions' />
            <Route component={Report} path='questions' />
            <Route component={Report} path='questions/:questionId'>
              <IndexRedirect to='students' />
              <Route component={Report} path='students' />
            </Route>
            <Route component={Report} path='students' />
            <Route component={Report} path='students/:studentId'>
              <IndexRedirect to='questions' />
              <Route component={Report} path='questions' />
            </Route>
          </Route>
          <Route component={Report} path='questions' />
          <Route component={Report} path='questions/:questionId'>
            <IndexRedirect to='assignments' />
            <Route component={Report} path='assignments' />
            <Route component={Report} path='assignments/:assignmentId'>
              <IndexRedirect to='students' />
              <Route component={Report} path='students' />
            </Route>
            <Route component={Report} path='students' />
            <Route component={Report} path='students/:studentId'>
              <IndexRedirect to='assignments' />
              <Route component={Report} path='assignments' />
            </Route>
          </Route>
          <Route component={Report} path='students' />
          <Route component={Report} path='students/:studentId'>
            <IndexRedirect to='questions' />
            <Route component={Report} path='questions' />
            <Route component={Report} path='questions/:questionId'>
              <IndexRedirect to='assignments' />
              <Route component={Report} path='assignments' />
            </Route>
            <Route component={Report} path='assignments' />
            <Route component={Report} path='assignments/:assignmentId'>
              <IndexRedirect to='questions' />
              <Route component={Report} path='questions' />
            </Route>
          </Route>
        </Route>
      </Route>
    </>
  );
};

export default (
  <Route component={Reports} path='/reports'>
    <IndexRoute component={ReportSelector} />

    <Route path=':topLevelFilterType(/:topLevelFilterId)' onEnter={maybeRedirectFromLegacyUrl}>
      <IndexRoute component={ReportSelector} />

      <Route component={ReportSelector} path='gradebook'>
        <IndexRoute component={Report} />

        {buildRoutes(false)}
      </Route>

      {buildRoutes(true)}

      {/*
        This is important because drilldown paths can fail to match without it
        For example /reports/<classroom-id>/assignments/<assigmnent-id>/students will match like this

        topLevelFilterType = <classroom-id>
        topLevelFilterId = assignments
        rest of url = <assignment-id>/students

        which will not match any of the above routes, so the redirect logic in maybeRedirectFromLegacyUrl
        would never get triggered because that URL would not match into ANY routes in this file.
      */}
      <Route path='*' component={ReportSelector} />
    </Route>
  </Route>
);
